import React from 'react';
import useStyles from '../../theme/useStyles';
import { FormLayout, Input, FileInput, Card } from '../../components/ui';
import type { SettingsSectionProps } from './SettingsPage';

// The church's identity — name, contact, address and logo. Everything entered
// here is applied as the header and letterhead on every certificate, payslip
// and generated PDF in the system.

// The logo is stored inline on the config row as base64 and travels with every
// config fetch, so it is capped tighter than an ordinary document upload.
// base64 adds roughly a third, so 1 MB on disk is about 1.4 MB on the wire.
const LOGO_MAX_BYTES = 1_000_000;

const IdentitySection = ({ form, setForm, saving, onSubmit, notify, t, isDarkMode }: SettingsSectionProps) => {
  const styles = useStyles(isDarkMode);

  return (
    <>
      <Card style={{ marginBottom: '20px', padding: '15px', color: t.textMuted, fontSize: t.fontSize.md }}>
        The details entered here are applied automatically as headers and letterheads
        across all official certificates, reports and generated PDFs in the system.
      </Card>

      <FormLayout
        title="Church Identity"
        submitLabel="💾 Save Identity"
        submitting={saving}
        onSubmit={onSubmit}
      >
        <FormLayout.Grid>
          <Input
            label="Official Church Name" required fullWidth
            placeholder="e.g. CSI CHRIST CHURCH, DEVICODE"
            value={form.churchName} onChange={(e) => setForm({ ...form, churchName: e.target.value })}
            style={{ fontSize: '18px', fontWeight: 'bold' }}
          />
          <Input
            label="Contact Number"
            placeholder="e.g. 04652 234567"
            value={form.contactNo} onChange={(e) => setForm({ ...form, contactNo: e.target.value.replace(/[^0-9+\- ]/g, '') })}
          />
          <Input
            label="Official Address" type="textarea" rows={3} fullWidth required
            placeholder="Full official address, as it should appear on letterheads..."
            value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <FileInput
            label="Church Logo"
            accept="image/*"
            maxBytes={LOGO_MAX_BYTES}
            hint="PNG or JPG, up to 1 MB. A wide, transparent PNG sits best on a letterhead."
            fileName={form.logoBase64 ? 'Logo uploaded' : ''}
            onFile={(name, dataUrl) => setForm((prev) => ({ ...prev, logoBase64: dataUrl }))}
            onError={(msg) => notify(msg, 'error')}
          />
          {form.logoBase64 && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
              <img
                src={form.logoBase64}
                alt="Church logo preview"
                style={{ height: '80px', objectFit: 'contain', border: `1px solid ${t.border}`, borderRadius: t.radius.md, padding: '4px' }}
              />
              <button
                type="button"
                style={styles.deleteBtn}
                onClick={() => setForm((prev) => ({ ...prev, logoBase64: '' }))}
              >
                Remove
              </button>
            </div>
          )}
        </FormLayout.Grid>
      </FormLayout>

    </>
  );
};

export default IdentitySection;
